import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import { fmtLocal, userTz } from "@/lib/time";
import {
    ArrowLeft, Building2, Plus, Loader2, Search, X, Pencil, Trash2, Star,
    Users, StickyNote, Link2, Receipt, CreditCard, UserCircle, Phone, Globe, MapPin,
    LayoutGrid, List, Mail,
} from "lucide-react";

const STATUS_STYLE = {
    lead: "border-sky-400/40 text-sky-300",
    active: "border-emerald-400/40 text-emerald-300",
    inactive: "border-white/15 text-slate-500",
};
const aud = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

const StatusChip = ({ status }) => (
    <span className={`px-2 py-0.5 border text-[9px] font-mono-tech uppercase tracking-widest ${STATUS_STYLE[status] || ""}`}>{status}</span>
);

const Field = ({ label, children }) => (
    <div>
        <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mb-1">{label}</label>
        {children}
    </div>
);
const inputCls = "w-full bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-600";

const EMPTY_COMPANY = { name: "", website: "", industry: "", phone: "", address: "", city: "", status: "lead", abn: "", billing_email: "", billing_address: "", payment_terms: "" };

const CompanyFormModal = ({ initial, onClose, onSaved }) => {
    const editing = Boolean(initial?.id);
    const [form, setForm] = useState({ ...EMPTY_COMPANY, ...(initial || {}) });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async () => {
        setBusy(true);
        try {
            const payload = Object.fromEntries(Object.entries(form).filter(([k]) => k in EMPTY_COMPANY));
            const { data } = editing
                ? await api.patch(`/crm/companies/${initial.id}`, payload)
                : await api.post("/crm/companies", payload);
            toast.success(editing ? "Company updated." : "Company created.");
            onSaved(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not save company."); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="crm-company-modal">
            <div className="bg-[#1B2E55] border border-white/10 p-8 max-w-2xl w-full my-8">
                <div className="flex items-center justify-between mb-6">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ {editing ? "Edit company" : "New company"}</span>
                    <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="crm-modal-close"><X size={16} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Company name *"><input value={form.name} onChange={set("name")} className={inputCls} data-testid="crm-form-name" /></Field>
                    <Field label="Status">
                        <select value={form.status} onChange={set("status")} className={inputCls} data-testid="crm-form-status">
                            {["lead", "active", "inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Industry"><input value={form.industry || ""} onChange={set("industry")} className={inputCls} data-testid="crm-form-industry" /></Field>
                    <Field label="Website"><input value={form.website || ""} onChange={set("website")} className={inputCls} data-testid="crm-form-website" /></Field>
                    <Field label="Phone"><input value={form.phone || ""} onChange={set("phone")} className={inputCls} data-testid="crm-form-phone" /></Field>
                    <Field label="City"><input value={form.city || ""} onChange={set("city")} className={inputCls} data-testid="crm-form-city" /></Field>
                    <div className="sm:col-span-2"><Field label="Address"><input value={form.address || ""} onChange={set("address")} className={inputCls} data-testid="crm-form-address" /></Field></div>
                    <Field label="ABN"><input value={form.abn || ""} onChange={set("abn")} className={inputCls} data-testid="crm-form-abn" /></Field>
                    <Field label="Billing email"><input value={form.billing_email || ""} onChange={set("billing_email")} className={inputCls} data-testid="crm-form-billing-email" /></Field>
                    <Field label="Billing address"><input value={form.billing_address || ""} onChange={set("billing_address")} className={inputCls} data-testid="crm-form-billing-address" /></Field>
                    <Field label="Payment terms"><input value={form.payment_terms || ""} onChange={set("payment_terms")} placeholder="e.g. 14 days" className={inputCls} data-testid="crm-form-payment-terms" /></Field>
                </div>
                <button onClick={save} disabled={busy || !form.name.trim()} data-testid="crm-form-save"
                    className="mt-6 w-full bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white py-3 text-xs font-semibold uppercase tracking-widest">
                    {busy ? <Loader2 size={14} className="animate-spin mx-auto" /> : editing ? "Save changes" : "Create company"}
                </button>
            </div>
        </div>
    );
};

const ContactsPanel = ({ company, onChanged }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", title: "" });
    const [adding, setAdding] = useState(false);
    const add = async () => {
        setAdding(true);
        try {
            await api.post(`/crm/companies/${company.id}/contacts`, { ...form, is_primary: company.contacts.length === 0 });
            setForm({ name: "", email: "", phone: "", title: "" });
            toast.success("Contact added.");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not add contact."); }
        finally { setAdding(false); }
    };
    const makePrimary = async (c) => {
        await api.patch(`/crm/contacts/${c.id}`, { name: c.name, is_primary: true });
        onChanged();
    };
    const remove = async (c) => {
        await api.delete(`/crm/contacts/${c.id}`);
        toast.success("Contact removed.");
        onChanged();
    };
    return (
        <section className="border border-white/10 p-5" data-testid="crm-contacts-panel">
            <div className="flex items-center gap-2 mb-4"><Users size={13} className="text-[#FF5A00]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Contacts ({company.contacts.length})</span></div>
            <div className="space-y-2 mb-4">
                {company.contacts.length === 0 && <div className="text-xs text-slate-600">No contacts yet.</div>}
                {company.contacts.map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center gap-3 border border-white/10 px-3 py-2" data-testid={`crm-contact-row-${c.id}`}>
                        <button onClick={() => !c.is_primary && makePrimary(c)} title={c.is_primary ? "Primary contact" : "Make primary"}
                            data-testid={`crm-contact-primary-${c.id}`}
                            className={c.is_primary ? "text-[#FF5A00]" : "text-slate-600 hover:text-[#FF5A00]"}><Star size={13} fill={c.is_primary ? "currentColor" : "none"} /></button>
                        <div className="flex-1 min-w-[140px]">
                            <div className="text-sm text-white">{c.name} {c.title && <span className="text-slate-500 text-xs">· {c.title}</span>}</div>
                            <div className="text-[10px] font-mono-tech text-slate-500">{c.email || "no email"} {c.phone && `· ${c.phone}`}</div>
                        </div>
                        <button onClick={() => remove(c)} data-testid={`crm-contact-delete-${c.id}`} className="text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="crm-contact-name" />
                <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="crm-contact-email" />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="crm-contact-phone" />
                <input placeholder="Role / title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="crm-contact-title" />
            </div>
            <button onClick={add} disabled={adding || !form.name.trim()} data-testid="crm-contact-add"
                className="mt-2 inline-flex items-center gap-1.5 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 py-2 text-[10px] font-semibold uppercase tracking-widest">
                <Plus size={11} /> Add contact
            </button>
        </section>
    );
};

const NotesPanel = ({ company, tz, onChanged }) => {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const add = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/crm/companies/${company.id}/notes`, { text });
            setText("");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not add note."); }
        finally { setBusy(false); }
    };
    return (
        <section className="border border-white/10 p-5" data-testid="crm-notes-panel">
            <div className="flex items-center gap-2 mb-4"><StickyNote size={13} className="text-[#FF5A00]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Internal notes</span></div>
            <div className="flex gap-1.5 mb-3">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="Add an internal note…" className={inputCls} data-testid="crm-note-input" />
                <button onClick={add} disabled={busy || !text.trim()} data-testid="crm-note-add"
                    className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 text-[10px] uppercase tracking-widest shrink-0">Add</button>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
                {company.notes.length === 0 && <div className="text-xs text-slate-600">No notes yet.</div>}
                {company.notes.map((n) => (
                    <div key={n.id} className="border border-white/10 px-3 py-2" data-testid={`crm-note-row-${n.id}`}>
                        <div className="text-[10px] font-mono-tech text-slate-500"><span className="text-white">{n.author_name}</span> · {fmtLocal(n.at, tz)}</div>
                        <div className="text-xs text-slate-300 mt-1 whitespace-pre-wrap">{n.text}</div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const ActivityPanel = ({ company, tz }) => {
    const [activity, setActivity] = useState(null);
    const [allCamps, setAllCamps] = useState([]);
    const [linkId, setLinkId] = useState("");
    const load = () => api.get(`/crm/companies/${company.id}/activity`).then(({ data }) => setActivity(data)).catch(() => setActivity({ campaigns: [], contracts: [], payments: [], confirmed_bookings: 0, total_paid_cents: 0 }));
    useEffect(() => {
        load();
        api.get("/campaigns").then(({ data }) => setAllCamps(data)).catch(() => setAllCamps([]));
    }, [company.id]);
    const link = async () => {
        if (!linkId) return;
        try {
            await api.post(`/crm/companies/${company.id}/link-campaign`, { campaign_id: linkId });
            toast.success("Campaign linked.");
            setLinkId("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not link campaign."); }
    };
    if (!activity) return <Loader2 size={16} className="animate-spin text-[#FF5A00]" />;
    const linkedIds = new Set(activity.campaigns.map((c) => c.id));
    return (
        <section className="border border-white/10 p-5 space-y-6" data-testid="crm-activity-panel">
            <div className="grid grid-cols-3 gap-3 text-center">
                {[["Campaigns", activity.campaigns.length], ["Confirmed bookings", activity.confirmed_bookings], ["Billed & paid", aud(activity.total_paid_cents)]].map(([k, v]) => (
                    <div key={k} className="border border-white/10 py-3">
                        <div className="text-xl font-display font-black text-white" data-testid={`crm-stat-${k.split(" ")[0].toLowerCase()}`}>{v}</div>
                        <div className="text-[9px] font-mono-tech uppercase tracking-widest text-slate-500 mt-1">{k}</div>
                    </div>
                ))}
            </div>

            <div>
                <div className="flex items-center gap-2 mb-2"><Link2 size={12} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Campaign history</span></div>
                {activity.campaigns.length === 0 && <div className="text-xs text-slate-600">No campaigns linked — link one below or add a contact whose email matches past campaigns.</div>}
                <div className="space-y-1.5">
                    {activity.campaigns.map((c) => (
                        <div key={c.id} className="flex flex-wrap items-center gap-3 border border-white/10 px-3 py-2 text-xs" data-testid={`crm-campaign-row-${c.id}`}>
                            <span className="flex-1 min-w-[140px] text-white">{c.business_name}</span>
                            <span className="text-slate-500 font-mono-tech">{c.launch_date}</span>
                            <span className="text-slate-400">${(c.budget_amount_aud || 0).toLocaleString()}</span>
                            <StatusChip status={c.pipeline_status === "cancelled" ? "inactive" : c.pipeline_status === "new" ? "lead" : "active"} />
                            <span className="text-[9px] font-mono-tech uppercase text-slate-600">{c.pipeline_status}</span>
                        </div>
                    ))}
                </div>
                <div className="mt-2 flex gap-1.5">
                    <select value={linkId} onChange={(e) => setLinkId(e.target.value)} className={inputCls} data-testid="crm-link-campaign-select">
                        <option value="">Link an existing campaign…</option>
                        {allCamps.filter((c) => !linkedIds.has(c.id)).map((c) => (
                            <option key={c.id} value={c.id}>{`${c.business_name} — ${c.pipeline_status}`}</option>
                        ))}
                    </select>
                    <button onClick={link} disabled={!linkId} data-testid="crm-link-campaign-btn"
                        className="bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-50 text-white px-3 text-[10px] uppercase tracking-widest shrink-0">Link</button>
                </div>
            </div>

            <div>
                <div className="flex items-center gap-2 mb-2"><Receipt size={12} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Quotes & contracts</span></div>
                {activity.contracts.length === 0 ? <div className="text-xs text-slate-600">No contracts yet.</div> : (
                    <div className="space-y-1.5">
                        {activity.contracts.map((k) => (
                            <div key={k.id} className="flex flex-wrap items-center gap-3 border border-white/10 px-3 py-2 text-xs" data-testid={`crm-contract-row-${k.contract_number}`}>
                                <span className="font-mono-tech text-white">{k.contract_number}</span>
                                <span className="text-slate-500">{k.issue_date}</span>
                                <span className="flex-1 text-right text-white">{aud(k.total_cents)}</span>
                                <span className="text-[9px] font-mono-tech uppercase tracking-widest text-slate-400 border border-white/15 px-1.5 py-0.5">{k.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center gap-2 mb-2"><CreditCard size={12} className="text-[#FF5A00]" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Payments</span></div>
                {activity.payments.length === 0 ? <div className="text-xs text-slate-600">No payments recorded.</div> : (
                    <div className="space-y-1.5">
                        {activity.payments.map((p, i) => (
                            <div key={i} className="flex items-center gap-3 border border-white/10 px-3 py-2 text-xs" data-testid={`crm-payment-row-${p.campaign_id}`}>
                                <span className="flex-1 text-white">{p.business_name}</span>
                                <span className="text-slate-500 font-mono-tech">{fmtLocal(p.paid_at, tz)}</span>
                                <span className="text-emerald-300">{aud(p.amount_cents)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

const CompanyDetail = ({ companyId, tz, onBack, onDeleted }) => {
    const [company, setCompany] = useState(null);
    const [staff, setStaff] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const load = () => api.get(`/crm/companies/${companyId}`).then(({ data }) => setCompany(data)).catch(() => { toast.error("Company not found."); onBack(); });
    useEffect(() => {
        load();
        api.get("/campaigns/meta/staff-options").then(({ data }) => setStaff(data.staff)).catch(() => setStaff([]));
    }, [companyId]);
    if (!company) return <Loader2 size={18} className="animate-spin text-[#FF5A00]" />;

    const setOwner = async (owner_id) => {
        await api.patch(`/crm/companies/${company.id}`, { owner_id: owner_id || null });
        toast.success("Account owner updated.");
        load();
    };
    const remove = async () => {
        if (!window.confirm(`Delete ${company.name} and all its contacts/notes?`)) return;
        await api.delete(`/crm/companies/${company.id}`);
        toast.success("Company deleted.");
        onDeleted();
    };

    return (
        <div data-testid="crm-company-detail">
            <button onClick={onBack} data-testid="crm-back-btn" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white mb-6">
                <ArrowLeft size={12} /> All companies
            </button>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h2 className="font-display font-black tracking-tighter text-3xl" data-testid="crm-detail-name">{company.name}</h2>
                        <StatusChip status={company.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-[11px] font-mono-tech text-slate-500">
                        {company.industry && <span>{company.industry}</span>}
                        {company.city && <span className="inline-flex items-center gap-1"><MapPin size={10} />{company.city}</span>}
                        {company.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{company.phone}</span>}
                        {company.website && <span className="inline-flex items-center gap-1"><Globe size={10} />{company.website}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/dashboard/comms?company=${company.id}`} data-testid="crm-emails-link"
                        className="border border-white/15 hover:border-white/40 text-white px-3 py-2 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"><Mail size={11} /> Emails</Link>
                    <div className="flex items-center gap-2 border border-white/10 px-3 py-2">
                        <UserCircle size={13} className="text-[#FF5A00]" />
                        <select value={company.owner_id || ""} onChange={(e) => setOwner(e.target.value)} data-testid="crm-owner-select"
                            className="bg-transparent text-xs text-white max-w-[180px]">
                            <option value="" className="bg-[#1B2E55]">No owner</option>
                            {staff.map((s) => <option className="bg-[#1B2E55]" key={s.id} value={s.id}>{`${s.name || s.email}`}</option>)}
                        </select>
                    </div>
                    <button onClick={() => setEditOpen(true)} data-testid="crm-edit-company-btn"
                        className="border border-white/15 hover:border-white/40 text-white px-3 py-2 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"><Pencil size={11} /> Edit</button>
                    <button onClick={remove} data-testid="crm-delete-company-btn"
                        className="border border-red-400/30 text-red-300 hover:bg-red-500/10 px-3 py-2 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"><Trash2 size={11} /> Delete</button>
                </div>
            </div>

            {(company.abn || company.billing_email || company.payment_terms || company.billing_address) && (
                <div className="mt-6 border border-white/10 p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs" data-testid="crm-billing-card">
                    {[["ABN", company.abn], ["Billing email", company.billing_email], ["Billing address", company.billing_address], ["Payment terms", company.payment_terms]].map(([k, v]) => (
                        <div key={k}><div className="text-[9px] font-mono-tech uppercase tracking-widest text-slate-500">{k}</div>
                            <div className="text-white mt-0.5">{v || "—"}</div></div>
                    ))}
                </div>
            )}

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <ContactsPanel company={company} onChanged={load} />
                    <NotesPanel company={company} tz={tz} onChanged={load} />
                </div>
                <ActivityPanel company={company} tz={tz} />
            </div>
            {editOpen && <CompanyFormModal initial={company} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
        </div>
    );
};

const BOARD_COLS = [["lead", "Leads"], ["active", "Active clients"], ["inactive", "Inactive"]];

const BoardView = ({ companies, onOpen, onMoved }) => {
    const [dragId, setDragId] = useState(null);
    const [overCol, setOverCol] = useState(null);
    const drop = async (status) => {
        const comp = companies.find((c) => c.id === dragId);
        setDragId(null); setOverCol(null);
        if (!comp || comp.status === status) return;
        try {
            await api.patch(`/crm/companies/${comp.id}`, { status });
            toast.success(`${comp.name} moved to ${status}.`);
            onMoved();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not move company."); }
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="crm-board">
            {BOARD_COLS.map(([col, label]) => {
                const items = companies.filter((c) => c.status === col);
                return (
                    <div key={col}
                        onDragOver={(e) => { e.preventDefault(); setOverCol(col); }}
                        onDragLeave={() => setOverCol(null)}
                        onDrop={() => drop(col)}
                        data-testid={`crm-board-col-${col}`}
                        className={`border p-3 min-h-[340px] transition-colors ${overCol === col ? "border-[#FF5A00]/60 bg-[#FF5A00]/5" : "border-white/10"}`}>
                        <div className="flex items-center justify-between mb-3">
                            <StatusChip status={col} />
                            <span className="text-[10px] font-mono-tech text-slate-500">{items.length}</span>
                        </div>
                        <div className="space-y-2">
                            {items.map((c) => (
                                <div key={c.id} draggable
                                    onDragStart={() => setDragId(c.id)}
                                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                                    onClick={() => onOpen(c.id)}
                                    data-testid={`crm-board-card-${c.id}`}
                                    className={`border border-white/10 bg-[#1B2E55]/60 px-3 py-2.5 cursor-grab active:cursor-grabbing hover:border-white/30 transition-colors ${dragId === c.id ? "opacity-40" : ""}`}>
                                    <div className="text-sm text-white font-semibold">{c.name}</div>
                                    <div className="mt-1 flex items-center gap-3 text-[9px] font-mono-tech text-slate-500">
                                        <span>{c.owner_name || "No owner"}</span>
                                        <span>{c.campaign_count} camp</span>
                                        <span>{c.contact_count} contact{c.contact_count === 1 ? "" : "s"}</span>
                                    </div>
                                </div>
                            ))}
                            {items.length === 0 && <div className="text-[10px] text-slate-600 text-center pt-8">Drop companies here</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default function Crm() {
    usePageMeta({ title: "CRM · NOXN", description: "Companies, contacts and account history." });
    const { user } = useAuth();
    const tz = userTz(user);
    const [companies, setCompanies] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [selected, setSelected] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [view, setView] = useState("list");

    const load = () => {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (status) params.status = status;
        api.get("/crm/companies", { params }).then(({ data }) => setCompanies(data.companies)).catch(() => setCompanies([]));
    };
    useEffect(() => { load(); }, [q, status]);

    return (
        <PortalShell title="CRM" mainClassName="p-0">
        <div className="min-h-screen bg-[#0F1E3B] text-white" data-testid="crm-page">
            <main className="mx-auto max-w-6xl px-6 lg:px-10 py-10">
                {selected ? (
                    <CompanyDetail companyId={selected} tz={tz} onBack={() => { setSelected(null); load(); }} onDeleted={() => { setSelected(null); load(); }} />
                ) : (
                    <>
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Building2 size={16} className="text-[#FF5A00]" />
                                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">Customer relationship management</span>
                                </div>
                                <h1 className="font-display font-black tracking-tighter text-4xl">Companies.</h1>
                            </div>
                            <button onClick={() => setCreateOpen(true)} data-testid="crm-new-company-btn"
                                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
                                <Plus size={13} /> New company
                            </button>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="relative">
                                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…"
                                    data-testid="crm-search-input"
                                    className="bg-[#1B2E55] border border-white/10 pl-9 pr-3 py-2 text-sm text-white w-64 placeholder:text-slate-600" />
                            </div>
                            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-status-filter"
                                disabled={view === "board"}
                                className="bg-[#1B2E55] border border-white/10 px-3 py-2 text-sm text-white disabled:opacity-40">
                                <option value="">All statuses</option>
                                {["lead", "active", "inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <div className="ml-auto flex border border-white/10">
                                {[["list", List], ["board", LayoutGrid]].map(([v, Icon]) => (
                                    <button key={v} onClick={() => setView(v)} data-testid={`crm-view-${v}`}
                                        title={v === "board" ? "Board view — drag companies between stages" : "List view"}
                                        className={`px-3 py-2 ${view === v ? "bg-[#FF5A00] text-white" : "text-slate-500 hover:text-white"}`}>
                                        <Icon size={14} />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6">
                            {!companies ? <Loader2 size={18} className="animate-spin text-[#FF5A00]" /> :
                                companies.length === 0 ? (
                                    <div className="border border-white/10 p-10 text-center" data-testid="crm-empty-state">
                                        <Building2 size={28} className="mx-auto text-slate-600" />
                                        <p className="mt-3 text-sm text-slate-500">No companies yet. Create your first company profile to start tracking contacts, campaigns and billing.</p>
                                    </div>
                                ) : view === "board" ? (
                                    <BoardView companies={companies} onOpen={setSelected} onMoved={load} />
                                ) : (
                                    <div className="border border-white/10 divide-y divide-white/5" data-testid="crm-companies-list">
                                        {companies.map((c) => (
                                            <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`crm-company-row-${c.id}`}
                                                className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-4 hover:bg-white/[0.03] transition-colors">
                                                <div className="flex-1 min-w-[180px]">
                                                    <div className="text-sm text-white font-semibold">{c.name}</div>
                                                    <div className="text-[10px] font-mono-tech text-slate-500">{[c.industry, c.city].filter(Boolean).join(" · ") || "—"}</div>
                                                </div>
                                                <StatusChip status={c.status} />
                                                <span className="text-[10px] font-mono-tech text-slate-500 w-24">{c.contact_count} contact{c.contact_count === 1 ? "" : "s"}</span>
                                                <span className="text-[10px] font-mono-tech text-slate-500 w-24">{c.campaign_count} campaign{c.campaign_count === 1 ? "" : "s"}</span>
                                                <span className="text-[10px] font-mono-tech text-slate-400 w-32 truncate">{c.owner_name || "No owner"}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                        </div>
                    </>
                )}
            </main>
            {createOpen && <CompanyFormModal onClose={() => setCreateOpen(false)} onSaved={(c) => { setCreateOpen(false); load(); setSelected(c.id); }} />}
        </div>
        </PortalShell>
    );
}
